import { createEffect } from '../utils/create-effect';

import { type PresenceEffectDeps } from './types';

/**
 * Selection machine effect that synchronizes the local user's telepointer
 * with the collaboration layer whenever the selection or focus changes.
 *
 * This is the new-selections-system replacement for the Redux-based
 * `presenceMiddleware`. Instead of reacting to Redux actions
 * (`selectValues`, `selectEntries`, `selectFields`), it reacts to
 * selection machine deltas and delegates index-to-ID translation to the
 * SelectionsApi.
 *
 * The effect tracks the last emitted telepointer position so that
 * duplicate updates (e.g. a drag that stays on the same cell) are
 * skipped, keeping the collab transport quiet.
 */
export const createPresenceEffect = createEffect(
  ({ selectionsApiFactory, collabStateManager, contentId }: PresenceEffectDeps) => {
    // Cache the last telepointer we sent so we can skip duplicate updates.
    let prevEntryId: string | undefined;
    let prevFieldId: string | undefined;

    return {
      deltaTypes: ['selectionReplaced', 'selectionModified', 'focusChanged'],

      dispose() {
        prevEntryId = undefined;
        prevFieldId = undefined;

        const collabManager = collabStateManager.getCollabManager(contentId);

        if (!collabManager) {
          return;
        }

        // make sure to clear the telepointer when this is disposed.
        collabManager.updateTelepointer(undefined, undefined);
      },

      onDelta(delta, state) {
        const collabManager = collabStateManager.getCollabManager(contentId);

        if (!collabManager) {
          return;
        }

        // Idle means everything was deselected — clear the telepointer
        // so other participants no longer see a cursor for this user.
        if (state.mode === 'idle') {
          if (prevEntryId !== undefined || prevFieldId !== undefined) {
            prevEntryId = undefined;
            prevFieldId = undefined;

            collabManager.updateTelepointer(undefined, undefined);
          }

          return;
        }

        // focusedCell is always set when mode is not idle — the machine
        // enforces this invariant. It represents the user's current position
        // regardless of how the selection changed (click, drag, Tab, etc.).
        // so, this is more of a TS guard
        if (!state.focusedCell) {
          return;
        }

        const selectionsApi = selectionsApiFactory();
        const { entryId, fieldId } = selectionsApi.resolvePositionToIds(
          state.focusedCell,
          state.mode
        );

        // Skip if the telepointer hasn't actually moved.
        if (entryId === prevEntryId && fieldId === prevFieldId) {
          return;
        }

        prevEntryId = entryId;
        prevFieldId = fieldId;

        collabManager.updateTelepointer(entryId, fieldId);
      },
    };
  }
);
